import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { getCategoryColumns, getSubcategoryColumns } from './columns';
import { CommonDatagrid } from '..';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { CategoriesService } from '../../../api/services/categoriesService';
import {
  setCategories,
  syncCategories,
  toggleCategory,
} from '../../../store/Reducers/CategoriesSlice';
import { ICategory, IRootState } from '../../../types/store';
import { showAlertDialog } from '../../../store/Reducers/AlertDialogSlice';
import { RefreshButton } from '../../Buttons/RefreshButton';

export const CategoryDatagrid = () => {
  const { content: data } = useSelector((state: IRootState) => state.categories);

  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [viewedCategory, setViewedCategory] = useState<ICategory | null>(null);

  // store unsaved uploads
  const [pendingUploads, setPendingUploads] = useState<Record<string, File>>({});

  const dispatch = useDispatch();

  const onToggleShowInApp = useCallback(
    async (category: ICategory) => {
      dispatch(toggleCategory({ ...category, show_inapp: !category.show_inapp }));
      try {
        const toggledCat = await CategoriesService.toggleShowinApp(category.name);
        dispatch(toggleCategory(toggledCat));
        dispatch(
          showNotification({
            message: `Successfully toggled category ${category.name}.`,
          }),
        );
      } catch (e: any) {
        dispatch(toggleCategory(category));
        dispatch(
          showNotification({
            message: `Failed to toggle category. ${e?.response?.data?.message || ''}`,
          }),
        );
      }
    },
    [dispatch],
  );

  const handleUpload = (rowId: string, file: File) => {
    setPendingUploads((prev) => ({
      ...prev,
      [rowId]: file,
    }));
  };

  const categoryColumns = useMemo(() => {
    return getCategoryColumns(setIsOpenDialog, setViewedCategory, onToggleShowInApp);
  }, [onToggleShowInApp]);

  const subcategoryColumns = useMemo(() => {
    return getSubcategoryColumns(handleUpload);
  }, []);

  const retrieveData = async () => {
    try {
      const res = await CategoriesService.getAllCategories();
      dispatch(setCategories(res));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to retrieve categories. ${e?.response?.data || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const syncCategoriesApi = async () => {
    try {
      const res = await CategoriesService.syncCategories();
      dispatch(syncCategories(res));

      dispatch(
        showNotification({
          message: `Synced categories from CIN7 successfully.`,
        }),
      );
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to sync categories. ${e?.response?.data || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const onClickSyncCategories = () => {
    dispatch(
      showAlertDialog({
        title: 'Sync Categories from Cin7?',
        description: 'This action cannot be undone.',
        confirmText: 'Sync',
        cancelText: 'Cancel',
        onConfirm: () => {
          syncCategoriesApi();
        },
      }),
    );
  };

  const syncSubcategoriesApi = async () => {
    try {
      const res = await CategoriesService.syncSubcategories();
      dispatch(syncCategories(res));

      dispatch(
        showNotification({
          message: `Synced Subcategories from CIN7 successfully.`,
        }),
      );
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to sync subcategories. ${e?.response?.data || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const onClickSyncSubcategories = () => {
    dispatch(
      showAlertDialog({
        title: 'Sync Subcategories from Cin7?',
        description: 'This action cannot be undone.',
        confirmText: 'Sync',
        cancelText: 'Cancel',
        onConfirm: () => {
          syncSubcategoriesApi();
        },
      }),
    );
  };

  const handleSaveAllImages = async () => {
    if (Object.keys(pendingUploads).length === 0) {
      dispatch(
        showNotification({
          message: 'No images to upload.',
          severity: 'warning',
        }),
      );
      return;
    }

    if (!viewedCategory?.name) {
      dispatch(
        showNotification({
          message: 'No category selected.',
          severity: 'error',
        }),
      );
      return;
    }

    try {
      const uploads = Object.entries(pendingUploads);

      for (const [subcategoryName, file] of uploads) {
        await CategoriesService.updateSubcatImage(viewedCategory.name, subcategoryName, file);
      }

      dispatch(
        showNotification({
          message: 'Images uploaded successfully.',
        }),
      );

      setPendingUploads({});
      setIsOpenDialog(false);

      // refresh categories so new images appear
      await retrieveData();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Upload failed. ${e?.response?.data || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const handleCloseDialog = () => {
    if (Object.keys(pendingUploads).length > 0) {
      dispatch(
        showAlertDialog({
          title: 'Discard uploads?',
          description:
            'You have uploaded images that are not saved. Are you sure you want to close?',
          confirmText: 'Discard',
          cancelText: 'Cancel',
          onConfirm: () => {
            setPendingUploads({});
            setIsOpenDialog(false);
          },
        }),
      );

      return;
    }

    setIsOpenDialog(false);
  };

  useEffect(() => {
    retrieveData();
  }, []);

  return (
    <>
      <CommonDatagrid
        header={
          <p>
            Categories are configured in Cin7 (Dear System) and synced to the app. Images are
            configured here.
          </p>
        }
        data={data}
        columns={categoryColumns}
        rowId={'name'}
        headerActionButtons={
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              justifyContent: 'flex-start',
              gap: 5,
            }}
          >
            <RefreshButton
              onClick={() => {
                retrieveData();
              }}
            />
            <Button
              style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
              variant="contained"
              onClick={onClickSyncCategories}
            >
              Sync Category
            </Button>

            <Button
              style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
              variant="contained"
              onClick={onClickSyncSubcategories}
            >
              Sync Subcategory
            </Button>
          </div>
        }
        searchbarPlaceholder={'Search category...'}
        searchKey="name"
      />

      <Dialog
        onClose={handleCloseDialog}
        open={isOpenDialog}
        slotProps={{
          paper: {
            sx: {
              width: '100%',
              maxWidth: 800,
              height: '80%',
              p: 2,
              borderRadius: 2,
              backgroundColor: '#fff',
              boxShadow: 24,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="h6">{viewedCategory?.name} Subcategories</Typography>

          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: 'grey.700' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <CommonDatagrid
          header={
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: 10,
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="contained"
                disabled={Object.keys(pendingUploads).length === 0}
                onClick={handleSaveAllImages}
                sx={{ height: 40 }}
              >
                Save All
              </Button>
            </div>
          }
          data={viewedCategory?.subcategories}
          columns={subcategoryColumns}
          rowId={'name'}
          headerActionButtons={<></>}
          searchbarPlaceholder={'Search subcategory'}
          searchKey="subcat"
        />
      </Dialog>
    </>
  );
};
